import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import Cropper, { Area } from 'react-easy-crop';
import { useLanguage } from '../../contexts/LanguageContext';

interface ImageCropperProps {
  imageSrc: string;
  fileName?: string;
  aspect?: number;
  onConfirm: (file: File) => void;
  onRetake: () => void;
  onCancel: () => void;
}

const getCroppedFile = async (
  imageSrc: string,
  pixelCrop: Area,
  rotation: number,
  fileName: string
): Promise<File | null> => {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });

  const radians = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  const rotatedWidth = image.width * cos + image.height * sin;
  const rotatedHeight = image.width * sin + image.height * cos;

  const rotatedCanvas = document.createElement('canvas');
  rotatedCanvas.width = rotatedWidth;
  rotatedCanvas.height = rotatedHeight;
  const rotatedCtx = rotatedCanvas.getContext('2d');
  if (!rotatedCtx) return null;
  rotatedCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
  rotatedCtx.rotate(radians);
  rotatedCtx.drawImage(image, -image.width / 2, -image.height / 2);

  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = pixelCrop.width;
  cropCanvas.height = pixelCrop.height;
  const cropCtx = cropCanvas.getContext('2d');
  if (!cropCtx) return null;
  cropCtx.drawImage(
    rotatedCanvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise<File | null>((resolve) => {
    cropCanvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        resolve(new File([blob], fileName, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.95
    );
  });
};

const ImageCropper: React.FC<ImageCropperProps> = ({
  imageSrc,
  fileName,
  aspect,
  onConfirm,
  onRetake,
  onCancel,
}) => {
  const { t } = useLanguage();
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels || processing) return;
    setProcessing(true);
    try {
      const finalName = fileName || `cropped_${Date.now()}.jpg`;
      const file = await getCroppedFile(imageSrc, croppedAreaPixels, rotation, finalName);
      if (file) {
        onConfirm(file);
      }
    } finally {
      setProcessing(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-black flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 bg-black bg-opacity-60">
        <button
          onClick={onCancel}
          className="text-white text-sm px-3 py-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          {t('cancel') || 'Cancel'}
        </button>
        <h3 className="text-white text-base font-semibold">
          {t('adjust_crop') || 'Adjust Crop'}
        </h3>
        <div className="w-16" />
      </div>

      <div className="relative flex-1 bg-black">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspect}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          showGrid={false}
          objectFit="contain"
          style={{
            cropAreaStyle: {
              border: '4px dashed rgba(255,255,255,0.85)',
              borderRadius: '12px',
              color: 'rgba(0,0,0,0.45)',
              boxShadow: '0 0 0 9999em',
            },
          }}
        />
      </div>

      <p className="text-center text-white text-xs opacity-75 px-4 pt-3">
        {t('crop_helper_text') || 'Drag to position, pinch or use the slider to zoom'}
      </p>

      <div className="px-6 pt-2 pb-1">
        <div className="flex items-center gap-3">
          <span className="text-white text-xs opacity-75 select-none">−</span>
          <input
            type="range"
            min={1}
            max={4}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-white"
            aria-label={t('zoom') || 'Zoom'}
          />
          <span className="text-white text-xs opacity-75 select-none">+</span>
          <button
            onClick={() => setRotation((r) => (r + 90) % 360)}
            disabled={processing}
            className="w-10 h-10 flex items-center justify-center rounded-lg text-white hover:bg-white/10 transition-colors disabled:opacity-50 ml-1"
            title={t('rotate') || 'Rotate'}
            aria-label={t('rotate') || 'Rotate'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      <div className="bg-black bg-opacity-60 px-4 py-4 flex items-center justify-between gap-3">
        <button
          onClick={onRetake}
          disabled={processing}
          className="flex-1 min-h-[48px] px-4 rounded-xl border border-white/40 text-white text-sm font-medium hover:bg-white/10 transition-colors disabled:opacity-50"
        >
          {t('retake') || 'Retake'}
        </button>

        <button
          onClick={handleConfirm}
          disabled={processing || !croppedAreaPixels}
          className="flex-1 min-h-[48px] px-4 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              <span>{t('processing') || 'Processing...'}</span>
            </>
          ) : (
            <span>{t('use_photo') || 'Use Photo'}</span>
          )}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default ImageCropper;
